import { addAction } from '@shared/utils/logger';
import i18n from './i18n';

// Private i18next internals, pinned against i18next 20.6.1 (see `Connector` in dist/cjs/i18next.js).
type BackendConnector = Readonly<{
  load: (languages: ReadonlyArray<string>, namespaces: ReadonlyArray<string>, callback: (err?: unknown) => void) => void;
  loaded: (name: string, err: unknown) => void;
  state: Readonly<Record<string, number>>;
}>;
type LanguageUtils = Readonly<{ toResolveHierarchy: (code: string) => string[] }>;
const LOAD_PENDING = 1;

/**
 * Deadline for one preload. i18next-http-backend sets no request timeout, so a stalled bundle
 * request would otherwise block the switch and any chunk waiting on it forever. Same budget as
 * one chunk attempt (`RETRY_ATTEMPT_TIMEOUT_MS` in loadableFromNamedExport.ts): a bundle is
 * fetched alongside its chunk and deserves no more patience than the chunk itself.
 */
export const LANGUAGE_PRELOAD_TIMEOUT_MS = 15000;

type PreloadFailureReason = 'error' | 'timeout' | 'throw';

const errorMessage = (err: unknown): string => {
  if (Array.isArray(err)) return err.map(errorMessage).join('; ');
  return String((err as { message?: string } | null | undefined)?.message ?? err);
};

const reportPreloadFailure = (lng: string, namespaces: ReadonlyArray<string>, reason: PreloadFailureReason, err: unknown): void => {
  addAction('I18nLanguagePreloadFailed', {
    lng,
    namespaces: namespaces.join(','),
    reason,
    message: errorMessage(err),
  });
};

/** i18next 20.6.1 sets this while `changeLanguage` is loading, but doesn't type it. */
const getLanguageChangingTo = (): string | undefined => (i18n as { isLanguageChangingTo?: string }).isLanguageChangingTo;

/** The target of a `switchLanguage` still preloading, before i18next knows about the switch. */
let pendingTarget: string | undefined;

/** The language the page is about to be in: a pending switch, an in-flight change, or the current one. */
export const getEffectiveLanguage = (): string | undefined => pendingTarget ?? getLanguageChangingTo() ?? i18n.language;

/**
 * Loads `namespaces` for `lng` without touching the current language. Always resolves: failures
 * are reported as `I18nLanguagePreloadFailed` and left as i18next's "loaded with error" state, so
 * `useTranslation` renders instead of waiting on them.
 *
 * i18next 20 has no public API for this: `loadLanguages` permanently adds `lng` to
 * `options.preload`, `reloadResources` refetches bundles already in the store. The connector's
 * `load` is what `changeLanguage` itself uses, and it dedupes against loaded/pending bundles.
 */
export const loadForLanguage = (lng: string, namespaces: ReadonlyArray<string>): Promise<void> => {
  const services = i18n.services as { backendConnector?: BackendConnector; languageUtils?: LanguageUtils } | undefined;
  const connector = services?.backendConnector;
  const languageUtils = services?.languageUtils;
  if (!connector || !languageUtils || namespaces.length === 0 || lng.toLowerCase() === 'cimode') {
    return Promise.resolve();
  }
  const uniqueNamespaces = [...new Set(namespaces)];
  return new Promise(resolve => {
    let settled = false;
    const settle = () => {
      settled = true;
      clearTimeout(timer);
      resolve();
    };
    const timer = setTimeout(() => {
      if (settled) return;
      settled = true;
      const languages = languageUtils.toResolveHierarchy(lng);
      const stalled = languages.flatMap(l => uniqueNamespaces.map(ns => `${l}|${ns}`)).filter(name => connector.state[name] === LOAD_PENDING);
      reportPreloadFailure(lng, uniqueNamespaces, 'timeout', `stalled after ${LANGUAGE_PRELOAD_TIMEOUT_MS}ms: ${stalled.join(',')}`);
      // Mark stalled bundles failed so `changeLanguage` and `useTranslation` stop waiting on them;
      // a response that arrives later still goes through `loaded()` and lands in the store.
      stalled.forEach(name => connector.loaded(name, new Error('i18n preload timed out')));
      settle();
    }, LANGUAGE_PRELOAD_TIMEOUT_MS);
    try {
      connector.load(languageUtils.toResolveHierarchy(lng), uniqueNamespaces, err => {
        if (settled) return;
        if (err) reportPreloadFailure(lng, uniqueNamespaces, 'error', err);
        settle();
      });
    } catch (err) {
      reportPreloadFailure(lng, uniqueNamespaces, 'throw', err);
      settle();
    }
  });
};

/** Resolves once an in-flight `changeLanguage` has landed, so ours is applied after it, not before. */
const waitForInFlightChange = (): Promise<void> => {
  if (getLanguageChangingTo() === undefined) return Promise.resolve();
  return new Promise(resolve => {
    const done = () => {
      i18n.off('languageChanged', done);
      clearTimeout(timer);
      resolve();
    };
    const timer = setTimeout(done, LANGUAGE_PRELOAD_TIMEOUT_MS);
    i18n.on('languageChanged', done);
  });
};

let latestSwitch = 0;

/**
 * `i18n.changeLanguage` that first loads, in the target language, every namespace the page already
 * uses. `changeLanguage` only loads `options.ns` as it stood when called, so a component rendered
 * with a namespace that arrived later would suspend right after the flip — and guest pages hydrate
 * with no root `<Suspense>`, so that suspension crashes into the error boundary.
 *
 * The latest call wins: a call superseded while preloading resolves without changing the language.
 * A change i18next has already started can't be cancelled, so it lands first and the newer target
 * is applied after it. Never rejects.
 */
export const switchLanguage = async (target: string): Promise<void> => {
  if (target === getEffectiveLanguage()) {
    return;
  }
  const token = ++latestSwitch;
  const isSuperseded = () => token !== latestSwitch;
  const current = i18n.language;
  pendingTarget = target;
  try {
    const configured = i18n.options?.ns ?? [];
    const namespacesInStore = current ? Object.keys(i18n.store?.data?.[current] ?? {}) : [];
    await loadForLanguage(target, [...(typeof configured === 'string' ? [configured] : configured), ...namespacesInStore]);
    if (isSuperseded()) return;
    await waitForInFlightChange();
    if (isSuperseded() || i18n.language === target) return;
    await i18n.changeLanguage(target);
  } catch (err) {
    reportPreloadFailure(target, [], 'throw', err);
  } finally {
    if (!isSuperseded()) pendingTarget = undefined;
  }
};

/** Test hook: forget pending and superseded switches left by a previous test. */
const resetSwitchState = (): void => {
  pendingTarget = undefined;
  latestSwitch = 0;
};

export const __testing__ = { resetSwitchState };
